import { useState } from "react";
import type { PermissionRequest, Provider, QuestionRequest } from "../types";
import { ModelSelect, type ModelChoice } from "./ModelSelect";

export function Modal({
  title,
  children,
  onClose,
  footer,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className={wide ? "modal wide" : "modal"}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" aria-label="Fechar" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

// ── Permissões ─────────────────────────────────────────────────────

export function PermissionsModal({
  requests,
  onReply,
  onAllowAllSession,
  saving,
  onClose,
}: {
  requests: PermissionRequest[];
  onReply: (id: string, reply: "once" | "always" | "reject", message?: string) => void;
  onAllowAllSession: (sessionID: string) => void;
  saving: boolean;
  onClose: () => void;
}) {
  const [messages, setMessages] = useState<Record<string, string>>({});
  const current = requests[0];
  if (!current) return null;

  return (
    <Modal
      title="Permissão solicitada pelo OpenCode"
      onClose={onClose}
      footer={
        <>
          <button className="btn danger" disabled={saving} onClick={() => onReply(current.id, "reject", messages[current.id])}>
            Negar
          </button>
          <button className="btn" disabled={saving} onClick={() => onReply(current.id, "once", messages[current.id])}>
            Permitir uma vez
          </button>
          <button className="btn primary" disabled={saving} onClick={() => onReply(current.id, "always", messages[current.id])}>
            Sempre permitir
          </button>
          <button
            className="btn primary"
            disabled={saving}
            onClick={() => onAllowAllSession(current.sessionID)}
            title="Salva a aprovação automática somente para a sessão desta solicitação"
          >
            {saving ? "Salvando…" : "Permitir tudo nesta sessão"}
          </button>
        </>
      }
    >
      <p>
        O agente solicita a permissão <strong>{current.permission}</strong> na sessão{" "}
        <code>{current.sessionID}</code>.
      </p>
      <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
        Ao permitir tudo nesta sessão, comandos e alterações em arquivos ficam sem confirmação,
        incluindo iniciar a aplicação. Outras sessões mantêm suas próprias permissões.
      </p>
      {current.patterns?.length ? (
        <div className="field">
          <label>Padrões de arquivo</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {current.patterns.map((p) => (
              <span key={p} className="chip">
                {p}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {current.metadata && Object.keys(current.metadata).length > 0 ? (
        <div className="field">
          <label>Detalhes da solicitação</label>
          <pre
            style={{
              background: "var(--bg-sunken)",
              border: "1px solid var(--border)",
              borderRadius: 10,
              padding: 12,
              overflow: "auto",
              maxHeight: 260,
              fontSize: 12,
            }}
          >
            {JSON.stringify(current.metadata, null, 2)}
          </pre>
        </div>
      ) : null}
      {current.tool ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Ferramenta: <code>{current.tool.callID}</code> (mensagem {current.tool.messageID})
        </p>
      ) : null}
      {current.always?.length ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          "Sempre permitir" aplica-se a: {current.always.join(", ")}
        </p>
      ) : null}
      <div className="field">
        <label htmlFor={`perm-msg-${current.id}`}>Mensagem opcional para o agente</label>
        <input
          id={`perm-msg-${current.id}`}
          className="input"
          value={messages[current.id] ?? ""}
          placeholder="Ex.: apenas dentro de src/"
          onChange={(e) => setMessages((m) => ({ ...m, [current.id]: e.target.value }))}
        />
      </div>
      {requests.length > 1 ? (
        <p className="chip warn">+{requests.length - 1} solicitação(ões) aguardando</p>
      ) : null}
    </Modal>
  );
}

// ── Perguntas ──────────────────────────────────────────────────────

export function QuestionsModal({
  requests,
  onReply,
  onReject,
  onClose,
}: {
  requests: QuestionRequest[];
  onReply: (id: string, answers: string[][]) => void;
  onReject: (id: string) => void;
  onClose: () => void;
}) {
  const current = requests[0];
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [custom, setCustom] = useState<Record<number, string>>({});
  if (!current) return null;

  const normalized: string[][] = current.questions.map((_, idx) => {
    const selected = answers[idx] ?? [];
    const free = custom[idx]?.trim();
    return free ? [...selected, free] : selected;
  });

  return (
    <Modal
      title="O OpenCode está perguntando"
      onClose={onClose}
      footer={
        <>
          <button className="btn danger" onClick={() => onReject(current.id)}>
            Rejeitar
          </button>
          <button
            className="btn primary"
            disabled={normalized.every((a) => a.length === 0)}
            onClick={() => onReply(current.id, normalized)}
          >
            Responder
          </button>
        </>
      }
    >
      {current.questions.map((q, idx) => (
        <div key={idx} className="field">
          <label>{q.header || q.question}</label>
          <p style={{ margin: "0 0 6px", fontSize: 13.5 }}>{q.question}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {q.options.map((opt) => {
              const selected = (answers[idx] ?? []).includes(opt.label);
              return (
                <label
                  key={opt.label}
                  className="tree-item"
                  style={{
                    cursor: "pointer",
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    padding: "8px 10px",
                  }}
                >
                  <input
                    type={q.multiple ? "checkbox" : "radio"}
                    name={`q-${current.id}-${idx}`}
                    checked={selected}
                    onChange={() =>
                      setAnswers((a) => {
                        const prev = a[idx] ?? [];
                        if (q.multiple) {
                          return {
                            ...a,
                            [idx]: selected ? prev.filter((x) => x !== opt.label) : [...prev, opt.label],
                          };
                        }
                        return { ...a, [idx]: [opt.label] };
                      })
                    }
                  />
                  <span style={{ fontFamily: "var(--font-sans)" }}>
                    <strong>{opt.label}</strong>
                    <span style={{ color: "var(--text-muted)" }}> — {opt.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
          {q.custom ? (
            <input
              className="input"
              placeholder="Ou digite sua própria resposta…"
              value={custom[idx] ?? ""}
              onChange={(e) => setCustom((c) => ({ ...c, [idx]: e.target.value }))}
            />
          ) : null}
        </div>
      ))}
    </Modal>
  );
}

// ── Nova sessão / renomear ─────────────────────────────────────────

export function NewSessionModal({
  providers,
  connected,
  agents,
  onCreate,
  onClose,
}: {
  providers: Provider[];
  connected?: string[];
  agents: Array<{ name: string; description?: string }>;
  onCreate: (data: {
    title?: string;
    agent?: string;
    model?: { id: string; providerID: string };
    allowAppRun?: boolean;
    allowAllPermissions?: boolean;
  }) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [agent, setAgent] = useState("");
  const [model, setModel] = useState<ModelChoice | null>(null);
  const [allowAppRun, setAllowAppRun] = useState(false);
  const [allowAllPermissions, setAllowAllPermissions] = useState(false);

  return (
    <Modal
      title="Nova sessão"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn primary"
            onClick={() => {
              onCreate({
                title: title.trim() || undefined,
                agent: agent || undefined,
                model: model ? { id: model.modelID, providerID: model.providerID } : undefined,
                allowAppRun,
                allowAllPermissions,
              });
            }}
          >
            Criar sessão
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="ns-title">Título (opcional)</label>
        <input
          id="ns-title"
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex.: Corrigir testes do módulo de autenticação"
          autoFocus
        />
      </div>
      <div className="field">
        <label htmlFor="ns-model">Modelo</label>
        <ModelSelect
          providers={providers}
          value={model}
          onChange={setModel}
          connected={connected}
          id="ns-model"
          ariaLabel="Modelo"
        />
      </div>
      <div className="field">
        <label htmlFor="ns-agent">Agente</label>
        <select id="ns-agent" className="select" value={agent} onChange={(e) => setAgent(e.target.value)}>
          <option value="">Padrão</option>
          {agents.map((a) => (
            <option key={a.name} value={a.name}>
              {a.name}
              {a.description ? ` — ${a.description}` : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={allowAllPermissions}
            aria-label="Permitir tudo nesta sessão"
            onChange={(e) => setAllowAllPermissions(e.target.checked)}
          />
          <span>
            <strong>Permitir tudo nesta sessão</strong> — comandos e alterações em arquivos ficam
            sem confirmação, incluindo iniciar a aplicação. Pode ser alterado depois nesta sessão.
          </span>
        </label>
      </div>
      <div className="field">
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={allowAllPermissions || allowAppRun}
            disabled={allowAllPermissions}
            aria-label="Rodar aplicação"
            onChange={(e) => setAllowAppRun(e.target.checked)}
          />
          <span>
            <strong>Rodar aplicação</strong>{allowAllPermissions
              ? " — já incluído em permitir tudo nesta sessão."
              : <> — permite subir a aplicação nesta sessão (ex.: <code>npm run dev</code>).
                Comandos comuns (instalar, compilar, testar) executam mesmo desmarcado.
                Pode ser alterado depois, por sessão.</>}
          </span>
        </label>
      </div>
    </Modal>
  );
}

export function RenameModal({
  current,
  onRename,
  onClose,
}: {
  current: string;
  onRename: (title: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(current);
  return (
    <Modal
      title="Renomear sessão"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => onRename(title.trim())}>
            Salvar
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="rn-title">Título</label>
        <input id="rn-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </div>
    </Modal>
  );
}

// ── Credencial de provedor (chave de API) ────────────────────────────

export function ProviderKeyModal({
  provider,
  saving,
  error,
  onSave,
  onClose,
}: {
  provider: { id: string; name: string; env?: string[] };
  saving: boolean;
  error?: string | null;
  onSave: (key: string) => void;
  onClose: () => void;
}) {
  const [key, setKey] = useState("");
  const envName = provider.env && provider.env.length > 0 ? provider.env[0] : null;
  return (
    <Modal
      title={`Chave de API — ${provider.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            className="btn primary"
            onClick={() => onSave(key.trim())}
            disabled={saving || key.trim().length === 0}
          >
            {saving ? "Salvando…" : "Salvar chave"}
          </button>
        </>
      }
    >
      <p>
        O provedor <strong>{provider.name}</strong> exige uma chave de API para usar seus modelos.
        A chave é gravada <strong>apenas no servidor</strong> (credencial do OpenCode) e não volta
        ao navegador depois de salva.
      </p>
      {envName ? (
        <p className="muted">
          Variável oficial equivalente: <code>{envName}</code>
        </p>
      ) : null}
      <div className="field">
        <label htmlFor="pk-key">Chave de API</label>
        <input
          id="pk-key"
          className="input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Cole a chave do provedor"
          autoComplete="off"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && key.trim().length > 0 && !saving) onSave(key.trim());
          }}
        />
      </div>
      {error ? <p className="error-text">{error}</p> : null}
    </Modal>
  );
}
